import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Loader2, CreditCard, Check, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MockPaymentPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [countdown, setCountdown] = useState(3);
  
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  const planId = searchParams.get('plan_id');
  
  useEffect(() => {
    // Auto-process after 3 seconds
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          handlePayment('success');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const handlePayment = async (status) => {
    if (status === 'cancel') {
      navigate('/pricing');
      return;
    }
    
    setProcessing(true);
    
    try {
      if (sessionId) {
        // Stripe payment
        await axios.post(
          `${BACKEND_URL}/api/payments/stripe/verify`,
          { session_id: sessionId },
          { withCredentials: true }
        );
      } else if (orderId) {
        // PayPal payment
        await axios.post(
          `${BACKEND_URL}/api/payments/paypal/verify`,
          { order_id: orderId },
          { withCredentials: true }
        );
      }
      
      navigate('/payment/success');
    } catch (error) {
      console.error('Payment error:', error);
      navigate('/payment/failed');
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/20 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center">
            {processing ? (
              'Processing Payment...'
            ) : (
              'Mock Payment Gateway'
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {processing ? (
            <div className="text-center py-8">
              <Loader2 className="w-16 h-16 mx-auto animate-spin text-purple-500 mb-4" />
              <p className="text-muted-foreground">Verifying your payment...</p>
            </div>
          ) : (
            <>
              {/* Mock Payment Info */}
              <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                <p className="text-sm font-semibold">🔧 Mock Payment Mode</p>
                <p className="text-xs text-muted-foreground">
                  This is a simulated payment for testing. No real money will be charged.
                </p>
                <p className="text-xs text-muted-foreground">
                  Payment Method: {sessionId ? 'Stripe' : 'PayPal'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Plan: {planId}
                </p>
              </div>
              
              {/* Auto-process countdown */}
              <div className="text-center py-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 dark:bg-purple-900 mb-3">
                  <span className="text-2xl font-bold text-purple-600 dark:text-purple-300">
                    {countdown}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Auto-processing in {countdown} seconds...
                </p>
              </div>
              
              {/* Manual buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={() => handlePayment('success')}
                  className="flex-1"
                  size="lg"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Complete Payment
                </Button>
                <Button
                  onClick={() => handlePayment('cancel')}
                  variant="outline"
                  size="lg"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MockPaymentPage;